import {
  collection,
  onSnapshot,
  orderBy,
  query,
  Unsubscribe,
} from "firebase/firestore";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { db } from "../../../config/firebase";
import { RootState } from "../../../store/rootReducer";
import {
  selectMessagesError,
  selectMessagesForConversation,
  selectMessagesLoadingStatus,
  setMessagesError,
  setMessagesLoading,
  setMessagesSuccess,
} from "../store/conversationMessagesSlice";
import { MessageWithId } from "../store/conversationMessagesSlice";
import { AgentContext } from "../types/agentConfig";
import { GenericAgentConversationService } from "../services/GenericAgentConversationService";

/**
 * Generic hook for managing conversation messages across different agent types
 */
export function useGenericConversationMessages(
  context: AgentContext,
  conversationId: string | null
) {
  const dispatch = useDispatch();

  const messages = useSelector((state: RootState) =>
    conversationId ? selectMessagesForConversation(state, conversationId) : []
  );
  const loadingStatus = useSelector((state: RootState) =>
    conversationId ? selectMessagesLoadingStatus(state, conversationId) : "idle"
  );
  const error = useSelector((state: RootState) =>
    conversationId ? selectMessagesError(state, conversationId) : null
  );

  useEffect(() => {
    if (!conversationId) return;

    dispatch(setMessagesLoading({ conversationId }));

    // Create service instance and get messages path
    const service = new GenericAgentConversationService(context.agentType);
    const messagesPath = service.getMessagesPath(context, conversationId);

    // Set up Firestore listener using the generic path
    const messagesRef = collection(db, messagesPath);
    const messagesQuery = query(messagesRef, orderBy("timestamp", "asc"));

    let unsubscribe: Unsubscribe;
    try {
      unsubscribe = onSnapshot(
        messagesQuery,
        (snapshot) => {
          const messages = snapshot.docs.map(
            (doc) =>
              ({
                id: doc.id,
                ...doc.data(),
              } as MessageWithId)
          );

          dispatch(
            setMessagesSuccess({
              conversationId,
              messages,
            })
          );
        },
        (error) => {
          console.error("Error fetching messages:", error);
          dispatch(
            setMessagesError({
              conversationId,
              error: error.message,
            })
          );
        }
      );
    } catch (error) {
      console.error("Error setting up messages listener:", error);
      dispatch(
        setMessagesError({
          conversationId,
          error: error instanceof Error ? error.message : "Unknown error",
        })
      );
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [dispatch, context.agentType, context.contextId, context.userId, conversationId]);

  return {
    messages,
    loadingStatus,
    error,
  };
}